import { Card, Modal, Spin, Tag, Typography } from "antd";
import queryClient from "api/queryClient.ts";
import { useGetTenantCatalogShowQuery } from "api/Tenant/hooks";
import { useProgramAccessAction } from "components/Tables/ProgramsCatalogTable/useProgramAccessAction";
import { Drawer } from "components/UI/Drawer";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";
import { DrawerId } from "types/models";
import { getDrawerSearchParams } from "utils/drawerHelpers";

interface ProgramDetailDrawerProps {
    tenantId: string;
}

export const ProgramDetailDrawer: FC<ProgramDetailDrawerProps> = ({ tenantId }) => {
    const { t } = usePaldockTranslation("drawers.program-detail");
    const { t: tCategory } = usePaldockTranslation("general.tenant_category");
    const { t: tProgramsCatalog } = usePaldockTranslation("tables.programs-catalog");
    const id = DrawerId.ProgramDetail;

    const request = { filter: { include: "catalogCategories" } };
    const { data, isLoading } = useGetTenantCatalogShowQuery(tenantId, request, { enabled: !!tenantId });
    const { tenant } = data || {};

    const { renderAccessAction, awaitingApprovalTenant, setAwaitingApprovalTenant } = useProgramAccessAction(() =>
        queryClient.invalidateQueries({ queryKey: ["getTenantCatalogShow", tenantId, request] })
    );

    return (
        <Drawer
            drawerId={id}
            title={<DrawerTitle mainTitle={tenant?.title ?? t("title")} />}
            searchParamsToClean={getDrawerSearchParams(id, "tenantId")}
            extraButtons={tenant ? renderAccessAction(tenant, "large") : undefined}
        >
            <DrawerContent>
                {isLoading ? (
                    <div className="flex justify-center items-center p-8">
                        <Spin />
                    </div>
                ) : (
                    <div className="flex flex-col gap-3 p-4">
                        {tenant?.logo && (
                            <div className="flex justify-center">
                                <img src={tenant.logo} alt={tenant.title} className="max-h-24 object-contain" />
                            </div>
                        )}

                        <Card size="small" title={t("categories")}>
                            {tenant?.catalogCategories && tenant.catalogCategories.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                    {tenant.catalogCategories.map((cat) => (
                                        <Tag key={cat.id}>{tCategory(cat.category)}</Tag>
                                    ))}
                                </div>
                            ) : (
                                <Typography.Text type="secondary">{t("no_categories")}</Typography.Text>
                            )}
                        </Card>
                    </div>
                )}
            </DrawerContent>

            <Modal
                open={awaitingApprovalTenant !== null}
                onCancel={() => setAwaitingApprovalTenant(null)}
                onOk={() => setAwaitingApprovalTenant(null)}
                title={awaitingApprovalTenant?.title}
                cancelButtonProps={{ style: { display: "none" } }}
            >
                {tProgramsCatalog("messages.request_submitted")}
            </Modal>
        </Drawer>
    );
};
